<template>
    <vx-chart-column-stacked
        legend
        :error="hasError"
        @error="chartError = true"
        :loading="loading"
        :data="dataComputed.data"
        loadingStyle="bars"
        :colors="dataComputed.colors"
        :axis="{
            x: {
                key: 'date',
                label: '',
                interval: 'day',
                ticks: 10,
                tickFormat: (value) => dayjs.utc(value).format('MM/DD'),
            },
            y: {
                key: 'count',
                rawKey: 'rawCount',
                ticks: 10,
                label: '',
                tickFormat: (value) => (Number.isInteger(value) ? formatCount(value) : ''),
            },
        }"
    >
        <template v-slot:title>
            <slot name="title"> Messages Sent by Day </slot>
        </template>

        <template v-slot:explainHeader>Messages Sent By Day</template>
        <template v-slot:explainContent>
            <p>Number of Messages (SMS & MMS) sent in the selected period of time distributed by Audience Type.</p>
            <router-button :to="{ name: 'campaigns' }" size="sm" color="muted">
                Go to Campaigns
                <font-awesome-icon widthAuto :icon="faArrowRight"></font-awesome-icon>
            </router-button>
        </template>
    </vx-chart-column-stacked>
</template>
<script setup>
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import RouterButton from '~/components/general/RouterButton.vue';
import { VxChartColumnStacked } from '@voxie/frontend-components';
import dayjs from '~/utils/dayjs';
import { ref, watch, computed } from 'vue';
import { outboundMessagesAnalyticsClient } from '~/services';
import { formatCount } from '~/components/filters';
import {
    calculateRangeBetweenTwoDates,
    generateLegendColors,
    lowestAndHighestExceptZeroes,
    metricsInputValidator,
} from '~/utils/analytics';
import { useVersionedRequest } from '~/composables/useVersionedRequest';
import { audienceTypeLabel } from '~/constants/message';

const props = defineProps({
    fetchParams: {
        type: Object,
        required: true,
        validator: metricsInputValidator,
    },
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
});

const chartError = ref(false);
const hasError = computed(() => {
    return Boolean(error.value) || chartError.value;
});

const computedRange = computed(() => {
    return calculateRangeBetweenTwoDates({
        startDate: props.fetchParams.range[0],
        endDate: props.fetchParams.range[1],
    });
});
const { loading, response, error, fetchData } = useVersionedRequest();
const dataComputed = computed(() => {
    const rawData = (response.value?.data?.data || []).sort((a, b) => b.audience_type.localeCompare(a.audience_type));

    const [lowestValue, highestValue] = lowestAndHighestExceptZeroes(
        rawData.reduce((values, audienceType) => values.concat(audienceType.values), [])
    );

    // only computes a new count when difference is bigger than 300 times than lowest value
    const shouldComputeCount = highestValue - lowestValue > 300 * lowestValue;

    // used in order to avoid bars that are too small to be seen
    const computedCount = Math.round(highestValue * 0.035);
    const groupedByDay = [];

    computedRange.value.forEach((date, index) => {
        if (index === computedRange.value.length - 1) {
            return;
        }

        const day = {
            date: date,
            data: [],
        };

        rawData.forEach((audienceType) => {
            const rawCount = audienceType.values[index];
            const count = shouldComputeCount && rawCount !== 0 ? Math.max(rawCount, computedCount) : rawCount;

            day.data.push({
                label: audienceTypeLabel(audienceType.audience_type),
                date: new Date(date),
                rawCount: rawCount,
                count: count,
                changedCount: rawCount !== count,
            });
        });

        day.data = day.data.sort((a, b) => a.rawCount - b.rawCount);

        if (day.data.some((item) => item.changedCount) && !day.data.every((item) => item.changedCount)) {
            const highestInDay = day.data[0];
            if (!highestInDay.changedCount) {
                // * 2 because day.data includes it on the reducer sum
                const adjustedCount =
                    highestInDay.count * 2 - day.data.reduce((sum, current) => sum + current.count, 0);
                day.data[0].count = adjustedCount > 0 ? adjustedCount : highestInDay.count;
            }
        }

        groupedByDay.push(day);
    });

    const data = groupedByDay.reduce((data, day) => data.concat(day.data), []);

    return {
        data: data.sort((a, b) => a.label.localeCompare(b.label)),
        colors: generateLegendColors(
            rawData.map((audienceType) => audienceTypeLabel(audienceType.audience_type)) || []
        ).sort((a, b) => b.label.localeCompare(a.label)),
    };
});
watch(
    [() => props.fetchParams],
    () => {
        chartError.value = false;

        const params = {
            ...props.fetchParams,
            range: computedRange.value,
        };

        fetchData(
            outboundMessagesAnalyticsClient.getTotalMessagesSentByAudienceType(
                {
                    teamId: Spark.state.currentTeam.id,
                    orgUnitId: props.orgUnitId,
                    groupId: props.groupId,
                },
                params
            )
        ).catch((e) => {
            console.error(e);
        });
    },
    { immediate: true, deep: true }
);
</script>
